import React from "react";

import { MenuAction } from "@tiny/components/src/DataTable/DataTable.types";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import {
  SubscriberColumnId,
  SubscribersTableProps,
} from "./SubscribersTable.types";
import { CustomerInfoView } from "@tiny/api";
import { visibleColumnsDefault } from "./SubscribersTable.helpers";
import { usePutCustomerUpdateStatusMutation } from "@tiny/api";

export function useSubscribersTable(props: SubscribersTableProps) {
  const {
    enableEdit,
    subscribers,
    onAfterEdit,
    onDelete,
    onColumnsSettingsClose,
    hideDelete,
  } = props;

  const { mutateAsync: updateCustomerStatus } =
    usePutCustomerUpdateStatusMutation();

  const { mdUp } = useBreakpoints();

  const [editOpen, setEditOpen] = React.useState(false);

  const [unsubscribeDialogOpen, setUnsubscribeDialogOpen] =
    React.useState(false);

  const [visibleColumns, setVisibleColumns] = React.useState<
    SubscriberColumnId[]
  >(() => {
    const visibleStored = localStorage.getItem("subscribers-table-columns");
    return visibleStored ? JSON.parse(visibleStored) : visibleColumnsDefault;
  });

  const [visibleCustomAttributes, setVisibleCustomAttributes] = React.useState<
    string[]
  >(() => {
    const stored = localStorage.getItem(
      "subscribers-table-custom-attr-columns"
    );
    return stored ? JSON.parse(stored) : [];
  });

  const [activeCustomer, setActiveCustomer] =
    React.useState<CustomerInfoView>();

  const [unsubscribeCustomer, setUnsubscribeCustomer] =
    React.useState<CustomerInfoView>();

  const editRow = React.useCallback(
    (row: CustomerInfoView) => {
      const customer = subscribers?.find((item) => item.id === row?.id);
      setActiveCustomer(customer);
      setEditOpen(true);
    },
    [subscribers]
  );

  const updateSubscriptionStatus = React.useCallback(
    async (row: CustomerInfoView) => {
      const isUnsubscribing = row.status === "Subscribed";

      if (isUnsubscribing) {
        setUnsubscribeCustomer(row);
        setUnsubscribeDialogOpen(true);
      } else {
        await updateCustomerStatus(
          {
            customerId: row.id!,
            status: "Subscribed",
            statusReason: undefined,
          },
          {
            onSuccess() {
              onAfterEdit();
            },
          }
        );
      }
    },
    [updateCustomerStatus, onAfterEdit]
  );

  const deleteRow = React.useCallback(
    (row: CustomerInfoView) => {
      const customer = subscribers?.find((item) => item.id === row?.id);
      onDelete(customer!);
    },
    [onDelete, subscribers]
  );

  const closeEdit = React.useCallback(() => {
    setEditOpen(false);
  }, []);

  const successEdit = React.useCallback(() => {
    setEditOpen(false);
    onAfterEdit();
  }, [onAfterEdit]);

  const closeUnsubscribeDialog = React.useCallback(() => {
    setUnsubscribeDialogOpen(false);
    setUnsubscribeCustomer(undefined);
  }, []);

  const successUnsubscribe = React.useCallback(() => {
    setUnsubscribeDialogOpen(false);
    setUnsubscribeCustomer(undefined);
    onAfterEdit();
  }, [onAfterEdit]);

  const applyColumnsSettings = React.useCallback(
    (selected: SubscriberColumnId[], customAttrs: string[]) => {
      localStorage.setItem(
        "subscribers-table-columns",
        JSON.stringify(selected)
      );
      localStorage.setItem(
        "subscribers-table-custom-attr-columns",
        JSON.stringify(customAttrs)
      );
      setVisibleColumns(selected);
      setVisibleCustomAttributes(customAttrs);
      onColumnsSettingsClose();
    },
    [onColumnsSettingsClose]
  );

  const moreActions: MenuAction<CustomerInfoView>[] = React.useMemo(() => {
    if (!enableEdit) {
      return [];
    }
    const editAction = {
      name: "Edit",
      onClickAction: editRow,
      visible: (row?: CustomerInfoView) => "Subscribed" === row?.status,
    };
    const deleteAction = { name: "Remove", onClickAction: deleteRow };
    const subscribeAction = {
      name: "Subscribe",
      visible: (row?: CustomerInfoView) =>
        row?.status === "Unsubscribed" &&
        row?.actionStatus !== "CustomerUnsubscribed",
      onClickAction: updateSubscriptionStatus,
    };
    const unSubscribeAction = {
      name: "Unsubscribe",
      visible: (row?: CustomerInfoView) =>
        row?.status === "Subscribed" &&
        row?.actionStatus !== "CustomerUnsubscribed",
      onClickAction: updateSubscriptionStatus,
    };
    return hideDelete
      ? [editAction]
      : [editAction, deleteAction, subscribeAction, unSubscribeAction];
  }, [enableEdit, editRow, deleteRow, hideDelete, updateSubscriptionStatus]);

  return {
    activeCustomer,
    closeEdit,
    editOpen,
    successEdit,
    moreActions,
    visibleColumns,
    visibleCustomAttributes,
    applyColumnsSettings,
    mdUp,
    unsubscribeDialogOpen,
    unsubscribeCustomer,
    closeUnsubscribeDialog,
    successUnsubscribe,
  };
}
